import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { ROLES, type Role, type UserView } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';
import { ApiError } from '@/lib/api-error.js';
import { dateTime } from '@/lib/format.js';
import { queryKeys } from '@/lib/query-keys.js';
import { Button } from '@/components/ui/Button.js';
import { PageHeader } from '@/components/ui/Card.js';
import { Badge, ROLE_LABELS, RoleBadge } from '@/components/ui/Badge.js';
import { DataTable, type Column } from '@/components/ui/DataTable.js';
import { Checkbox, Input, Select } from '@/components/ui/Field.js';
import { ConfirmDialog, Modal } from '@/components/ui/Modal.js';
import { useToast } from '@/components/ui/Toast.js';

const ROLE_OPTIONS = ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }));

/** Foydalanuvchilar — faqat `superadmin` (TZ 3.2). */
export function UsersPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [creating, setCreating] = useState(false);
  const [passwordFor, setPasswordFor] = useState<UserView | null>(null);
  const [deleting, setDeleting] = useState<UserView | null>(null);

  const params = { page, limit: 20, search: search || undefined, role: role || undefined };
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => api.get<ListResult<UserView>>('/users', params),
  });

  const toggleActive = useMutation({
    mutationFn: (user: UserView) => api.patch(`/users/${user._id}`, { isActive: !user.isActive }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('Saqlandi');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (user: UserView) => api.delete(`/users/${user._id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      setDeleting(null);
      toast.success('Foydalanuvchi o‘chirildi');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const columns: Column<UserView>[] = [
    {
      key: 'name',
      header: 'Foydalanuvchi',
      render: (user) => (
        <div>
          <p className="font-medium text-n-900">{user.fullName}</p>
          <p className="text-xs text-n-600">{user.email}</p>
        </div>
      ),
    },
    { key: 'role', header: 'Rol', render: (user) => <RoleBadge role={user.role} /> },
    {
      key: 'status',
      header: 'Holat',
      render: (user) => (
        <Badge tone={user.isActive ? 'success' : 'neutral'}>
          {user.isActive ? 'Faol' : 'Bloklangan'}
        </Badge>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Oxirgi kirish',
      hideOnTablet: true,
      render: (user) => <span className="text-n-600">{dateTime(user.lastLoginAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (user) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleActive.mutate(user)}
            title={user.isActive ? 'Bloklash' : 'Blokdan chiqarish'}
          >
            {user.isActive ? 'Bloklash' : 'Ochish'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Parolni almashtirish"
            onClick={() => setPasswordFor(user)}
          >
            <KeyRound className="size-4" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="O‘chirish"
            onClick={() => setDeleting(user)}
          >
            <Trash2 className="size-4 text-error-600" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Foydalanuvchilar"
        description="Tizimga kira oladigan xodimlar va ularning huquqlari"
        action={
          <Button icon={<Plus className="size-4" aria-hidden />} onClick={() => setCreating(true)}>
            Yangi foydalanuvchi
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-52">
          <Select
            label="Rol"
            placeholder="Hammasi"
            options={ROLE_OPTIONS}
            value={role}
            onChange={(event) => {
              setRole(event.target.value as Role | '');
              setPage(1);
            }}
          />
        </div>
        <div className="min-w-56 flex-1">
          <Input
            label="Qidiruv"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Ism yoki elektron pochta"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(user) => user._id}
        loading={isLoading}
        meta={data?.meta}
        onPageChange={setPage}
        emptyTitle="Foydalanuvchi topilmadi"
      />

      <CreateUserModal open={creating} onClose={() => setCreating(false)} />

      {passwordFor && (
        <ResetPasswordModal user={passwordFor} onClose={() => setPasswordFor(null)} />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Foydalanuvchini o‘chirish"
        message={
          <>
            <strong>{deleting?.fullName}</strong> tizimdan butunlay o‘chiriladi. Uning jurnaldagi
            yozuvlari saqlanib qoladi.
          </>
        }
        confirmLabel="O‘chirish"
        danger
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/users', body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      setErrors({});
      onClose();
      toast.success('Foydalanuvchi yaratildi');
    },
    onError: (error: Error) => {
      // Maydon xatolari formada ko'rsatiladi — foydalanuvchi qaysi
      // maydonni tuzatish kerakligini bilishi kerak.
      if (error instanceof ApiError) {
        const fields = error.fieldErrors;
        if (Object.keys(fields).length > 0) {
          setErrors(fields);
          return;
        }
      }
      toast.error(error.message);
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      email: String(form.get('email') ?? '').trim(),
      password: String(form.get('password') ?? ''),
      fullName: String(form.get('fullName') ?? '').trim(),
      role: String(form.get('role') ?? 'editor'),
      isActive: form.get('isActive') === 'on',
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Yangi foydalanuvchi">
      <form id="create-user" onSubmit={onSubmit} className="space-y-4">
        <Input name="fullName" label="F.I.SH" required error={errors.fullName} />
        <Input name="email" type="email" label="Elektron pochta" required error={errors.email} />
        <Input
          name="password"
          type="password"
          label="Parol"
          required
          error={errors.password}
          hint="Kamida 10 belgi, katta va kichik harf hamda raqam bo‘lishi kerak"
          autoComplete="new-password"
        />
        <Select name="role" label="Rol" options={ROLE_OPTIONS} defaultValue="editor" required />
        <Checkbox name="isActive" label="Faol" defaultChecked />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Bekor qilish
          </Button>
          <Button type="submit" loading={create.isPending}>
            Yaratish
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserView; onClose: () => void }) {
  const toast = useToast();
  const [error, setError] = useState<string | undefined>();

  const reset = useMutation({
    mutationFn: (newPassword: string) => api.post(`/users/${user._id}/password`, { newPassword }),
    onSuccess: () => {
      onClose();
      toast.success('Parol almashtirildi. Foydalanuvchining barcha seanslari tugatildi');
    },
    onError: (err: Error) => {
      if (err instanceof ApiError) {
        setError(err.fieldErrors.newPassword ?? err.message);
        return;
      }
      toast.error(err.message);
    },
  });

  return (
    <Modal open onClose={onClose} title={`${user.fullName} — parolni almashtirish`} size="sm">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          reset.mutate(String(form.get('newPassword') ?? ''));
        }}
        className="space-y-4"
      >
        <Input
          name="newPassword"
          type="password"
          label="Yangi parol"
          required
          error={error}
          hint="Almashtirilgach foydalanuvchi barcha qurilmalarda qayta kirishi kerak"
          autoComplete="new-password"
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Bekor qilish
          </Button>
          <Button type="submit" loading={reset.isPending}>
            Almashtirish
          </Button>
        </div>
      </form>
    </Modal>
  );
}
